'use client';

import { type ReactNode } from 'react';
import { Logo, Link } from '@/components/ui';
import { NavIcon, type IconName } from '@/components/ui/icon';

/**
 * Shared visual primitives for the application sidebar.
 *
 * These are the LEAF renderers extracted from `SideNav.tsx` so other sidebars
 * (e.g. the club portal's `ClubShell`) can reuse the exact same chrome instead
 * of duplicating class strings. They hold NO navigation state — callers compute
 * `active`/labels/hrefs and pass them in. SideNav keeps its parent/child
 * expansion logic; only the per-item, header, and footer visuals live here.
 */

export interface SideNavItemLinkProps {
  href: string;
  label: string;
  icon: IconName;
  /** Whether this item is the active route. */
  active?: boolean;
  /** Collapsed (icon-only) rail rendering. */
  collapsed?: boolean;
  /** Indented child item (only meaningful when not collapsed). */
  isChild?: boolean;
  /** Renders the "coming soon" pill next to the label / in the tooltip. */
  comingSoon?: boolean;
  /** Localised "coming soon" text — required when `comingSoon` is set. */
  comingSoonLabel?: string;
  /** Renders a plain external `<a target="_blank">` instead of a `Link`. */
  external?: boolean;
  /** Opens in a new tab (internal links only). */
  openInNewTab?: boolean;
  /**
   * Softer active treatment used for an expanded parent row so its active child
   * keeps focal attention. Ignored unless `active` is true.
   */
  expandedParent?: boolean;
  onClick?: () => void;
  prefetch?: boolean;
  /** `aria-current` value applied to internal links (e.g. 'page'). */
  ariaCurrent?: React.AriaAttributes['aria-current'];
  /**
   * Override for the active leaf recipe (default: the app's amber
   * `bg-primary/15 text-foreground border-primary/30`). Lets a surface
   * (e.g. the club portal) re-accent active items without forking the row.
   */
  activeClassName?: string;
}

/**
 * One sidebar nav row — the `<li>` wrapper plus its internal `Link`/external
 * `<a>`. Renders byte-identically to SideNav's historical `renderNavItem`
 * output for a leaf item (and supports the expanded-parent active variant so
 * SideNav can use it for parents too).
 */
export function SideNavItemLink({
  href,
  label,
  icon,
  active = false,
  collapsed = false,
  isChild = false,
  comingSoon = false,
  comingSoonLabel,
  external = false,
  openInNewTab = false,
  expandedParent = false,
  onClick,
  prefetch = false,
  ariaCurrent,
  activeClassName = 'bg-primary/15 text-foreground border-primary/30',
}: SideNavItemLinkProps) {
  const activeClasses = expandedParent ? 'text-foreground font-medium' : activeClassName;

  const linkClassName = `group flex h-10 items-center rounded-md border border-transparent transition-colors ${
    collapsed ? 'mx-auto w-12 justify-center px-0 text-center' : 'justify-start px-3'
  } ${isChild && !collapsed ? 'h-9' : ''} ${
    active ? activeClasses : 'hover:bg-foreground/[0.06] text-foreground/80 hover:border-accent/30'
  } `;

  const content = collapsed ? (
    <>
      <span className="text-foreground/80 inline-flex items-center justify-center">
        <NavIcon name={icon} className="h-5 w-5" />
        <span className="sr-only">{label}</span>
      </span>
      {/* Hover tooltip for collapsed state */}
      <span className="pointer-events-none absolute top-1/2 left-full ml-2 -translate-y-1/2 rounded-md bg-gray-900 px-3 py-1.5 text-sm whitespace-nowrap text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 dark:bg-gray-100 dark:text-gray-900">
        {label}
        {comingSoon && (
          <span className="ml-2 rounded bg-white/20 px-1.5 py-0.5 text-[10px]">
            {comingSoonLabel}
          </span>
        )}
      </span>
    </>
  ) : (
    <span className={`inline-flex items-center gap-2 ${isChild ? 'text-sm' : ''}`}>
      <NavIcon name={icon} className="h-5 w-5" />
      {label}
      {comingSoon && (
        <span className="bg-foreground/10 text-foreground/70 rounded px-1.5 py-0.5 text-[10px]">
          {comingSoonLabel}
        </span>
      )}
    </span>
  );

  const liClassName =
    isChild && !collapsed ? 'relative ml-3 border-l-2 border-border/60 pl-3' : 'relative';

  return (
    <li className={liClassName}>
      {external ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={collapsed ? label : undefined}
          aria-label={collapsed ? label : undefined}
          onClick={onClick}
          className={linkClassName}
        >
          {content}
        </a>
      ) : (
        <Link
          href={href}
          prefetch={prefetch}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          title={collapsed ? label : undefined}
          aria-label={collapsed ? label : undefined}
          aria-current={ariaCurrent}
          onClick={onClick}
          variant={active ? 'filled' : 'subtle'}
          color={active ? 'primary' : 'default'}
          size="sm"
          className={linkClassName}
        >
          {content}
        </Link>
      )}
    </li>
  );
}

export interface SideNavHeaderProps {
  /** Logo size — 'lg' when expanded, 'md' when collapsed (SideNav convention). */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the wordmark text shows next to the glyph. */
  showText?: boolean;
  /** Optional link target for the logo (defaults to the locale home). */
  href?: string;
  onLogoClick?: () => void;
  /** Extra branding rendered next to the logo (e.g. a portal pill). */
  children?: ReactNode;
  /**
   * Extra classes for the Logo link — lets a surface re-accent the brand mark
   * (e.g. the club portal's navy ring via `[&>div]:ring-2`). Default: none.
   */
  logoClassName?: string;
}

/**
 * The sidebar logo row. Renders the `Logo` and, when `children` are supplied,
 * wraps logo+children in a horizontal group so callers can add branding (the
 * club portal adds its "Club portal" pill here).
 */
export function SideNavHeader({
  size = 'lg',
  showText = true,
  href,
  onLogoClick,
  children,
  logoClassName,
}: SideNavHeaderProps) {
  const logo = (
    <Logo
      size={size}
      showText={showText}
      href={href}
      onClick={onLogoClick}
      className={logoClassName}
    />
  );

  if (!children) return logo;

  return (
    <div className="flex items-center gap-2">
      {logo}
      {children}
    </div>
  );
}

export interface SideNavFooterProps {
  /** Collapsed rail uses tighter vertical padding. */
  collapsed?: boolean;
  children: ReactNode;
}

/**
 * The bordered footer container at the bottom of the sidebar. Children-driven
 * so SideNav can place its user-email / login-out controls inside and ClubShell
 * can place its user-email line inside — both share the border + padding.
 */
export function SideNavFooter({ collapsed = false, children }: SideNavFooterProps) {
  return <div className={`border-border border-t ${collapsed ? 'py-2' : 'py-4'}`}>{children}</div>;
}

export interface SideNavCollapseToggleProps {
  /** Whether the sidebar is currently collapsed (controls icon + labels). */
  collapsed: boolean;
  onToggle: () => void;
  /** Label/title when collapsed (SideNav passes 'Expand sidebar'). */
  expandLabel: string;
  /** Label/title when expanded (SideNav passes 'Collapse sidebar'). */
  collapseLabel: string;
}

/**
 * The expand/collapse rail toggle button. Renders byte-identically to SideNav's
 * historical inline button — the only difference is the aria-label/title come in
 * via props (SideNav keeps passing its English strings; ClubShell passes
 * localised text). Icon is the double-chevron, direction driven by `collapsed`.
 */
export function SideNavCollapseToggle({
  collapsed,
  onToggle,
  expandLabel,
  collapseLabel,
}: SideNavCollapseToggleProps) {
  return (
    <button
      onClick={onToggle}
      className="hover:bg-foreground/[0.06] flex h-8 w-8 items-center justify-center rounded-md transition-colors"
      aria-label={collapsed ? expandLabel : collapseLabel}
      title={collapsed ? expandLabel : collapseLabel}
    >
      {collapsed ? (
        // Expand icon (double right arrows)
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        </svg>
      ) : (
        // Collapse icon (double left arrows)
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      )}
    </button>
  );
}

export interface SideNavControlsProps {
  /** Collapsed pass-through to ThemeToggle (matches SideNav's wiring). */
  collapsed?: boolean;
  /** Localised "Theme" label. */
  themeLabel: string;
  /** Localised "Language" label. */
  languageLabel: string;
  /** The ThemeToggle element (callers import the concrete component). */
  themeToggle: ReactNode;
  /** The LanguageSwitcher element (callers import the concrete component). */
  languageSwitcher: ReactNode;
}

/**
 * The theme + language control block. Renders the two labelled rows with the
 * exact wrappers SideNav historically used. SideNav only mounts this when not
 * collapsed (its block is `!collapsed`-gated) so the `collapsed` prop is passed
 * straight through to ThemeToggle as before.
 */
export function SideNavControls({
  collapsed: _collapsed = false,
  themeLabel,
  languageLabel,
  themeToggle,
  languageSwitcher,
}: SideNavControlsProps) {
  return (
    <div className="mt-auto mb-6 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-muted text-sm">{themeLabel}</span>
        {themeToggle}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted text-sm">{languageLabel}</span>
        {languageSwitcher}
      </div>
    </div>
  );
}

export interface SideNavAuthFooterProps {
  /** Collapsed rail rendering (icon-only logout/login with tooltip). */
  collapsed?: boolean;
  isAuthenticated: boolean;
  /** Signed-in email (shown when authenticated + expanded). */
  email: string;
  /** Localised labels — SideNav passes its `t('Nav.…')` values. */
  labels: { logout: string; login: string; notLoggedIn: string };
  /** Current locale, for the login link target. */
  locale: string;
  onLogout: () => void;
  /** Pass-through for the login link's click (SideNav's onClose). */
  onNavigate?: () => void;
}

/**
 * The contents that live INSIDE `SideNavFooter`: the signed-in email (or
 * not-signed-in label) line, plus the logout button — or, when not
 * authenticated, the login link. Both have a collapsed icon-only variant with a
 * hover tooltip. Class strings are byte-identical to SideNav's historical
 * footer (incl. the gray tooltip — intentionally NOT retokenised here).
 */
export function SideNavAuthFooter({
  collapsed = false,
  isAuthenticated,
  email,
  labels,
  locale,
  onLogout,
  onNavigate,
}: SideNavAuthFooterProps) {
  return (
    <>
      {!collapsed && (
        <div className="text-foreground/60 truncate px-3 pb-2 text-xs">
          {isAuthenticated ? email : labels.notLoggedIn}
        </div>
      )}

      {isAuthenticated ? (
        <button
          type="button"
          onClick={onLogout}
          title={collapsed ? labels.logout : undefined}
          aria-label={collapsed ? labels.logout : undefined}
          className={`group border-border text-foreground/90 hover:bg-foreground/[0.06] inline-flex items-center justify-center rounded-md border text-sm transition-colors ${
            collapsed ? 'relative mx-auto h-10 w-12' : 'mx-3 mb-1 h-8 w-[calc(100%-1.5rem)]'
          }`}
        >
          {collapsed ? (
            <>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {/* Hover tooltip */}
              <span className="pointer-events-none absolute top-1/2 left-full ml-2 -translate-y-1/2 rounded-md bg-gray-900 px-3 py-1.5 text-sm whitespace-nowrap text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 dark:bg-gray-100 dark:text-gray-900">
                {labels.logout}
              </span>
            </>
          ) : (
            labels.logout
          )}
        </button>
      ) : (
        <Link
          href={`/${locale}/login`}
          onClick={onNavigate}
          title={collapsed ? labels.login : undefined}
          aria-label={collapsed ? labels.login : undefined}
          variant="outlined"
          color="default"
          size="sm"
          className={`group ${collapsed ? 'relative mx-auto flex h-10 w-12 items-center justify-center px-0' : 'mx-3 w-[calc(100%-1.5rem)]'}`}
        >
          {collapsed ? (
            <>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                />
              </svg>
              {/* Hover tooltip */}
              <span className="pointer-events-none absolute top-1/2 left-full ml-2 -translate-y-1/2 rounded-md bg-gray-900 px-3 py-1.5 text-sm whitespace-nowrap text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 dark:bg-gray-100 dark:text-gray-900">
                {labels.login}
              </span>
            </>
          ) : (
            labels.login
          )}
        </Link>
      )}
    </>
  );
}
